import { useMutation, useQuery } from "@tanstack/react-query";
import { useAdminApplication, useAdminSession } from "../../app/providers";
import type { Schemas } from "../workspace/api.generated";
import {
  createNotificationCampaign,
  createNotificationPolicy,
  createNotificationTemplate,
  createSingleNotification,
  getNotificationCampaigns,
  getNotificationDetail,
  getNotificationPolicies,
  getNotifications,
  getNotificationTemplates,
  getRegisteredNotificationTemplates,
  getRegisteredNotificationTemplate,
  retryNotification,
  setNotificationPolicyActive,
  setNotificationTemplateActive,
  updateNotificationPolicy,
  updateNotificationTemplate,
} from "./api";
import type { NotificationListQuery } from "./api";

export const notificationKeys = {
  all: (epoch: number) => ["admin", epoch, "notifications"] as const,
  lists: (epoch: number) => [...notificationKeys.all(epoch), "list"] as const,
  list: (epoch: number, query: NotificationListQuery) => [...notificationKeys.lists(epoch), query] as const,
  detail: (epoch: number, id: number) => [...notificationKeys.all(epoch), "detail", id] as const,
  templates: (epoch: number) => [...notificationKeys.all(epoch), "templates"] as const,
  policies: (epoch: number) => [...notificationKeys.all(epoch), "policies"] as const,
  campaigns: (epoch: number) => [...notificationKeys.all(epoch), "campaigns"] as const,
};

export function useNotificationList(query: NotificationListQuery, enabled: boolean) {
  const { apiClient } = useAdminApplication();
  const session = useAdminSession();
  return useQuery({
    queryKey: notificationKeys.list(session.sessionEpoch, query),
    queryFn: ({ signal }) => getNotifications(apiClient, query, signal),
    enabled,
    staleTime: 0,
  });
}

export function useNotificationDetail(id?: number) {
  const { apiClient } = useAdminApplication();
  const session = useAdminSession();
  return useQuery({
    queryKey: notificationKeys.detail(session.sessionEpoch, id ?? 0),
    queryFn: ({ signal }) => getNotificationDetail(apiClient, id!, signal),
    enabled: id !== undefined,
    staleTime: 0,
  });
}

export function useNotificationTemplates(enabled: boolean) {
  const { apiClient } = useAdminApplication();
  const session = useAdminSession();
  return useQuery({ queryKey: notificationKeys.templates(session.sessionEpoch), queryFn: ({ signal }) => getNotificationTemplates(apiClient, signal), enabled, staleTime: 0 });
}

export function useRegisteredNotificationTemplates(page: number, keyword: string, enabled: boolean) {
  const { apiClient } = useAdminApplication();
  const session = useAdminSession();
  return useQuery({ queryKey: [...notificationKeys.all(session.sessionEpoch), "registered", page, keyword], queryFn: ({ signal }) => getRegisteredNotificationTemplates(apiClient, page, keyword, signal), enabled, retry: false, staleTime: 0 });
}

export function useRegisteredNotificationTemplate(templateCode?: string, senderKeyType = "S") {
  const { apiClient } = useAdminApplication();
  const session = useAdminSession();
  return useQuery({ queryKey: [...notificationKeys.all(session.sessionEpoch), "registered-detail", templateCode, senderKeyType], queryFn: ({ signal }) => getRegisteredNotificationTemplate(apiClient, templateCode!, senderKeyType, signal), enabled: Boolean(templateCode), retry: false, staleTime: 0 });
}

export function useNotificationPolicies(enabled: boolean) {
  const { apiClient } = useAdminApplication();
  const session = useAdminSession();
  return useQuery({ queryKey: notificationKeys.policies(session.sessionEpoch), queryFn: ({ signal }) => getNotificationPolicies(apiClient, signal), enabled, staleTime: 0 });
}

export function useNotificationCampaigns(enabled: boolean) {
  const { apiClient } = useAdminApplication();
  const session = useAdminSession();
  return useQuery({ queryKey: notificationKeys.campaigns(session.sessionEpoch), queryFn: ({ signal }) => getNotificationCampaigns(apiClient, signal), enabled, staleTime: 0 });
}

export type NotificationMutation =
  | { readonly kind: "single"; readonly body: Schemas["CreateSingleNotificationRequest"] }
  | { readonly kind: "retry"; readonly id: number }
  | { readonly kind: "template-create"; readonly body: Schemas["CreateNotificationTemplateRequest"] }
  | { readonly kind: "template-update"; readonly id: number; readonly body: Schemas["CreateNotificationTemplateRequest"] }
  | { readonly kind: "template-active"; readonly id: number; readonly active: boolean }
  | { readonly kind: "policy-create"; readonly body: Schemas["CreateNotificationPolicyRequest"] }
  | { readonly kind: "policy-update"; readonly id: number; readonly body: Schemas["CreateNotificationPolicyRequest"] }
  | { readonly kind: "policy-active"; readonly id: number; readonly active: boolean }
  | { readonly kind: "campaign"; readonly body: Schemas["CreateNotificationCampaignRequest"] };

export function useNotificationMutation() {
  const { apiClient, queryClient, sessionLifecycle } = useAdminApplication();
  const session = useAdminSession();
  return useMutation({
    mutationFn: async (input: NotificationMutation) => {
      const request = sessionLifecycle.createRequestController();
      try {
        switch (input.kind) {
          case "single": return await createSingleNotification(apiClient, input.body, request.controller.signal);
          case "retry": return await retryNotification(apiClient, input.id, request.controller.signal);
          case "template-create": return await createNotificationTemplate(apiClient, input.body, request.controller.signal);
          case "template-update": return await updateNotificationTemplate(apiClient, input.id, input.body, request.controller.signal);
          case "template-active": return await setNotificationTemplateActive(apiClient, input.id, input.active, request.controller.signal);
          case "policy-create": return await createNotificationPolicy(apiClient, input.body, request.controller.signal);
          case "policy-update": return await updateNotificationPolicy(apiClient, input.id, input.body, request.controller.signal);
          case "policy-active": return await setNotificationPolicyActive(apiClient, input.id, input.active, request.controller.signal);
          case "campaign": return await createNotificationCampaign(apiClient, input.body, request.controller.signal);
        }
      } finally {
        request.release();
      }
    },
    onSuccess: async (_result, input) => {
      if (input.kind === "retry" || input.kind === "single") {
        await queryClient.invalidateQueries({ queryKey: notificationKeys.lists(session.sessionEpoch), refetchType: "none" });
      }
      if (input.kind === "retry") {
        await queryClient.invalidateQueries({ queryKey: notificationKeys.detail(session.sessionEpoch, input.id), refetchType: "none" });
      }
      if (input.kind.startsWith("template")) {
        await queryClient.invalidateQueries({ queryKey: notificationKeys.templates(session.sessionEpoch), refetchType: "none" });
      }
      if (input.kind.startsWith("policy")) {
        await queryClient.invalidateQueries({ queryKey: notificationKeys.policies(session.sessionEpoch), refetchType: "none" });
      }
      if (input.kind === "campaign") {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: notificationKeys.campaigns(session.sessionEpoch), refetchType: "none" }),
          queryClient.invalidateQueries({ queryKey: notificationKeys.lists(session.sessionEpoch), refetchType: "none" }),
        ]);
      }
    },
  });
}
